import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import {
  Loader2, ChevronLeft, ShieldCheck, Users, Coins, Heart, Bell, BellOff,
  Edit3, Trash2, Archive, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Avatar } from "@/components/AppHeader";
import { timeAgo } from "@/components/Cards";

const STATUS_META = {
  planned:     { ar: "مخطّط",     he: "מתוכנן",   en: "Planned",     color: "#06B6D4", bg: "#CFFAFE" },
  in_progress: { ar: "قيد التنفيذ", he: "בביצוע",    en: "In progress", color: "#F59E0B", bg: "#FEF3C7" },
  completed:   { ar: "مكتمل",     he: "הושלם",    en: "Completed",   color: "#10B981", bg: "#D1FAE5" },
  archived:    { ar: "مؤرشف",     he: "מאורכב",   en: "Archived",    color: "#6B7280", bg: "#E5E7EB" },
};

const fmt = (n) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n || 0);

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useI18n();
  const [project, setProject] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const isMunicipality = user && (user.role === "admin" || user.role === "municipality_admin");
  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/comments/project/${id}`).catch(() => ({ data: [] })),
        ]);
        setProject(p.data);
        setComments(c.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const pct = useMemo(() => {
    if (!project || !project.goal_amount) return 0;
    return Math.min(100, Math.round((project.raised_amount / project.goal_amount) * 100));
  }, [project]);

  const toggleFollow = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/follow`);
      setProject((p) => ({ ...p, is_following: data.following, followers_count: data.followers_count }));
      toast.success(data.following ? L("تتابع المشروع", "אתה עוקב", "Now following") : L("ألغيت المتابعة", "ביטלת מעקב", "Unfollowed"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const donate = async () => {
    const raw = window.prompt(L("كم تودّ التبرع (₪)؟", "כמה תרצה לתרום (₪)?", "How much would you like to donate (₪)?"), "50");
    const amount = parseInt(raw || "0", 10);
    if (!amount || amount < 1) return;
    try {
      const { data } = await api.post(`/projects/${id}/donate`, { amount });
      setProject(data);
      toast.success(L(`شكراً لتبرّعك بـ ₪${amount}`, `תודה על תרומה של ₪${amount}`, `Thanks for donating ₪${amount}`));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.patch(`/projects/${id}`, { status });
      setProject(data);
      toast.success(L("تم تحديث الحالة", "הסטטוס עודכן", "Status updated"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async () => {
    if (!window.confirm(L("أرشِف هذا المشروع؟", "לארכב את הפרויקט?", "Archive this project?"))) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success(L("تمت الأرشفة", "ארכוב הושלם", "Archived"));
      navigate("/community");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/comments/project/${id}`, { content: newComment.trim() });
      setComments((cs) => [...cs, data]);
      setNewComment("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const topBar = (
    <div className="h-14 px-3 flex items-center gap-2">
      <button
        onClick={() => navigate(-1)}
        data-testid="project-back-btn"
        className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
      >
        <ChevronLeft size={20} />
      </button>
      <h1 className="font-extrabold text-[18px] text-[var(--te-text)] flex-1 truncate" dir="auto">
        {project?.title || L("مشروع", "פרויקט", "Project")}
      </h1>
      {isMunicipality && project && (
        <>
          <Link
            to={`/projects/${id}/edit`}
            data-testid="project-edit-btn"
            className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
            title={L("تعديل", "ערוך", "Edit")}
          >
            <Edit3 size={15} />
          </Link>
          <button
            onClick={archive}
            data-testid="project-archive-btn"
            className="w-9 h-9 rounded-full bg-[var(--te-danger-soft)] hover:bg-[#FED7D7] flex items-center justify-center text-[var(--te-danger)]"
            title={L("أرشفة", "ארכוב", "Archive")}
          >
            <Archive size={15} />
          </button>
        </>
      )}
    </div>
  );

  if (loading) {
    return <MobileShell topBar={topBar}><div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div></MobileShell>;
  }
  if (!project) {
    return <MobileShell topBar={topBar}><div className="text-center py-24 text-[var(--te-text-secondary)]">{L("المشروع غير موجود", "הפרויקט לא נמצא", "Project not found")}</div></MobileShell>;
  }

  const meta = STATUS_META[project.status] || STATUS_META.planned;

  return (
    <MobileShell topBar={topBar}>
      <article className="te-fade-up">
        {project.image_url && (
          <img src={project.image_url} alt="" className="w-full h-56 object-cover" />
        )}
        <div className="px-4 pt-4 pb-6 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
              style={{ background: meta.bg, color: meta.color }}
              data-testid="project-status-badge"
            >
              {meta[language] || meta.en}
            </span>
            {project.official && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--te-primary)]" data-testid="project-verified-badge">
                <ShieldCheck size={13} /> {L("بلدي رسمي", "עירוני רשמי", "Verified municipality")}
              </span>
            )}
          </div>

          <h2 className="font-extrabold text-[22px] text-[var(--te-text)] leading-tight" dir="auto">{project.title}</h2>
          {project.address && (
            <div className="flex items-center gap-1.5 text-sm text-[var(--te-text-secondary)]" dir="auto">
              <MapPin size={14} /> {project.address}
            </div>
          )}

          <p className="text-[15px] text-[var(--te-text)] leading-relaxed whitespace-pre-wrap" dir="auto">
            {project.description}
          </p>

          {/* Stats card */}
          <div className="te-card p-4" data-testid="project-stats">
            <div className="flex items-baseline justify-between mb-2">
              <div className="font-extrabold text-[20px] text-[var(--te-text)]" dir="ltr">
                ₪{fmt(project.raised_amount)}
              </div>
              <div className="text-[12px] text-[var(--te-text-muted)]" dir="ltr">
                / ₪{fmt(project.goal_amount)} · <span className="font-bold text-[var(--te-primary)]">{pct}%</span>
              </div>
            </div>
            <div className="w-full h-2.5 rounded-full bg-[var(--te-input-bg)] overflow-hidden">
              <div className="h-full bg-[var(--te-primary)] transition-all" style={{ width: `${pct}%` }} data-testid="project-progress-bar" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="text-center">
                <div className="font-extrabold text-[18px] text-[var(--te-text)]" data-testid="project-supporters-count">{project.supporters_count}</div>
                <div className="text-[11px] text-[var(--te-text-muted)]">{L("داعمون", "תומכים", "supporters")}</div>
              </div>
              <div className="text-center">
                <div className="font-extrabold text-[18px] text-[var(--te-text)]" data-testid="project-followers-count">{project.followers_count}</div>
                <div className="text-[11px] text-[var(--te-text-muted)]">{L("متابعون", "עוקבים", "followers")}</div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={donate}
              data-testid="project-donate-btn"
              className="te-btn-primary flex-1 justify-center !py-3"
            >
              <Coins size={15} /> {L("ادعم المشروع", "תרום", "Donate")}
            </button>
            <button
              onClick={toggleFollow}
              data-testid="project-follow-btn"
              className={`flex-1 justify-center !py-3 rounded-full font-semibold text-[14px] inline-flex items-center gap-1.5 border ${
                project.is_following
                  ? "bg-[var(--te-primary-soft)] text-[var(--te-primary)] border-[var(--te-primary)]/30"
                  : "bg-white text-[var(--te-text)] border-[var(--te-divider)] hover:bg-[var(--te-bg-soft)]"
              }`}
            >
              {project.is_following ? <BellOff size={14} /> : <Bell size={14} />}
              {project.is_following ? L("تتابع", "עוקב", "Following") : L("تابِع", "עקוב", "Follow")}
            </button>
          </div>

          {/* Donations note */}
          <div className="text-[11px] text-[var(--te-text-muted)] text-center px-2" data-testid="project-donate-note">
            {L(
              "وضع تجريبي للتبرعات — لن تُخصم أي مبالغ حالياً.",
              "מצב הדגמה לתרומות — לא יחויב סכום בפועל כרגע.",
              "Donations are in demo mode — no real charges yet."
            )}
          </div>

          {/* Admin status selector */}
          {isMunicipality && (
            <div className="te-card p-3.5" data-testid="project-admin-controls">
              <div className="text-[11px] font-semibold text-[var(--te-text-secondary)] uppercase tracking-wider mb-2">
                {L("لوحة البلدية", "פאנל עירוני", "Municipality controls")}
              </div>
              <div className="flex flex-wrap gap-2">
                {["planned", "in_progress", "completed"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    data-testid={`project-set-status-${s}`}
                    className={`text-[12px] font-semibold px-3 py-1.5 rounded-full border ${
                      project.status === s
                        ? "bg-[var(--te-primary)] text-white border-[var(--te-primary)]"
                        : "bg-white text-[var(--te-text)] border-[var(--te-divider)]"
                    }`}
                  >
                    {STATUS_META[s][language] || STATUS_META[s].en}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <section className="mt-2">
            <h3 className="font-extrabold text-[16px] text-[var(--te-text)] mb-3">
              {L("التعليقات", "תגובות", "Comments")} ({comments.length})
            </h3>
            <form onSubmit={submitComment} className="te-card p-3 mb-3 flex gap-3">
              <Avatar user={user} size={36} />
              <div className="flex-1">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={L("شارك رأيك…", "שתף את דעתך…", "Share your thoughts…")}
                  className="te-input min-h-[60px] resize-y text-sm"
                  data-testid="project-comment-input"
                  dir="auto"
                />
                <div className="flex justify-end mt-2">
                  <button type="submit" disabled={busy || !newComment.trim()} className="te-btn-primary text-[13px] !py-2 !px-4" data-testid="project-comment-submit">
                    {busy ? <Loader2 size={13} className="animate-spin" /> : null}
                    {L("نشر", "פרסם", "Post")}
                  </button>
                </div>
              </div>
            </form>
            {comments.length === 0 ? (
              <div className="text-center text-sm text-[var(--te-text-muted)] py-6">
                {L("كن أول من يعلّق.", "היה הראשון להגיב.", "Be the first to comment.")}
              </div>
            ) : (
              <div className="space-y-2.5">
                {comments.map((c) => (
                  <div key={c.id} className="te-card p-3 flex gap-3" data-testid={`project-comment-${c.id}`}>
                    <Avatar user={c.author} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-[var(--te-text)]" dir="auto">{c.author?.name}</div>
                      <div className="text-[11px] text-[var(--te-text-muted)] mb-1">{timeAgo(c.created_at)}</div>
                      <p className="text-[13px] text-[var(--te-text)] whitespace-pre-wrap" dir="auto">{c.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </article>
    </MobileShell>
  );
}
